import { createSelector } from 'reselect'
import type { PlaylistId } from 'types/playlist'
import type { State } from 'types/redux'
import type {
  Shortlist,
  ShortlistSection,
  ShortlistTrackItem,
  TempItem,
} from 'types/shortlist'
import type { Track } from 'types/track'
import type { User } from 'types/user'
import { TRACK_NESTING } from '@disco/constants/FeatureTypes'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getTracks } from '@disco/state-management/selectors/tracks'
import { getUser } from '@disco/state-management/selectors/user'
const EMPTY_ARRAY = []
/**
 * Gets the shortlists object from state.
 * @param state - The Redux state
 * @returns Object mapping shortlist IDs to shortlist objects
 */
export const getShortlistsObject = (
  state: State,
): { [key: string]: Shortlist | undefined } => state.shortlists.byId

/**
 * Checks if any shortlists have uploading items.
 * @param shortlists - Array of shortlist objects
 * @returns True if any shortlist has uploading items
 */
const hasUploading = (shortlists: Shortlist[]): boolean =>
  shortlists.some(shortlist => hasUploadingInShortlist(shortlist))

/**
 * Gets the number of uploading items in a shortlist.
 * @param shortlist - The shortlist object
 * @returns Number of uploading (temp) items
 */
const numUploadingInShortlist = (shortlist: Shortlist): number =>
  shortlist.tempItems?.length ?? 0

/**
 * Checks if a shortlist has any uploading items.
 * @param shortlist - The shortlist object
 * @returns True if the shortlist has uploading items
 */
const hasUploadingInShortlist = (shortlist: Shortlist): boolean =>
  numUploadingInShortlist(shortlist) > 0

/**
 * Counts the number of uploading tracks in all hidden shortlists.
 * @param state - The Redux state
 * @returns Total count of uploading tracks in hidden shortlists
 */
export const numHiddenShortlistUploads = createSelector(
  getShortlistsObject,
  shortlists => {
    return Object.keys(shortlists)
      .map(id => shortlists[id])
      .filter(
        (s): s is Shortlist =>
          s != null && !!s.hidden && !!s.uploadCompletionTask,
      )
      .map(numUploadingInShortlist)
      .reduce((a, b) => a + b, 0)
  },
)

/**
 * Gets a shortlist by ID without refreshing track data.
 * @param state - The Redux state
 * @param props - Object containing the shortlist ID
 * @param props.id - The shortlist ID to retrieve
 * @returns The shortlist object with potentially stale track data
 */
const getShortlistByIdWithStaleTracks = (
  state: State,
  {
    id,
  }: {
    id: string | null | undefined
  },
): Shortlist | null | undefined =>
  id != null ? state.shortlists.byId[id] : null

/**
 * Gets a shortlist by ID with fresh track data.
 * @param state - The Redux state
 * @param props - Object containing the shortlist ID
 * @param props.id - The shortlist ID to retrieve
 * @returns The shortlist object with up-to-date track data
 */
export const getShortlistById: (
  arg0: State,
  arg1: {
    id: string | null | undefined
  },
) => Shortlist | null | undefined = createSelector(
  [getTracks, getShortlistByIdWithStaleTracks],
  (tracks, shortlist) => {
    if (!shortlist) return null
    return {
      ...shortlist,
      items: (shortlist.items ?? []).map(item => ({
        ...item,
        track: !item.track.tempId ? tracks[item.track.id] : item.track,
      })),
    }
  },
)
/**
 * Gets a shortlist by its associated playlist ID.
 * @param state - The Redux state
 * @param playlist_id - The playlist ID to find shortlist for
 * @returns The shortlist associated with the playlist, or null if not found
 */
export const getShortlistByPlaylistId: (
  state: State,
  playlist_id: PlaylistId,
) => Shortlist | null | undefined = createSelector(
  getShortlistsObject,
  (_, playlist_id) => playlist_id,
  (shortlistObject, playlist_id) => {
    if (!playlist_id) return null
    const shortlists: Shortlist[] = Object.keys(shortlistObject)
      .map((id: string) => shortlistObject[id])
      .filter((s): s is Shortlist => s != null)
    const result: Shortlist | null | undefined = shortlists.find(
      shortlist => shortlist?.playlist_id === playlist_id,
    )
    if (!result) return null
    return result
  },
)
/**
 * Factory function that creates a shortlist selector by ID.
 * @returns A selector function for getting shortlists by ID
 */
export const makeGetShortlistById = (): typeof getShortlistById =>
  getShortlistById
/**
 * Gets a specific section from a shortlist.
 * @param state - The Redux state
 * @param props - Object containing shortlist and section IDs
 * @param props.shortlistId - The shortlist ID
 * @param props.sectionId - The section ID to retrieve
 * @returns The shortlist section, or null if not found
 */
export const getShortlistSection = (
  state: State,
  {
    shortlistId,
    sectionId,
  }: {
    shortlistId: string
    sectionId: string
  },
): ShortlistSection | null | undefined => {
  const shortlist = getShortlistByIdWithStaleTracks(state, {
    id: shortlistId,
  })
  if (!shortlist) return null
  const section = shortlist.sections.find(s => s.id === sectionId)
  return section
}
/**
 * Gets the ID of the first visible shortlist.
 * @param props - Object containing shortlists state
 * @param props.shortlists - The shortlists state object
 * @returns The first visible shortlist ID, or null if none visible
 */
export const getFirstVisibleShortlistId = ({
  shortlists,
}: {
  shortlists: State['shortlists']
}): string | null | undefined =>
  shortlists.visibleItems.length > 0 ? shortlists.visibleItems[0] : null
/**
 * Gets items from a specific shortlist.
 * @param state - The Redux state
 * @param props - Object containing the shortlist ID
 * @param props.shortlistId - The shortlist ID to get items from
 * @returns Array of shortlist track items
 */
export const getShortlistItems = (
  state: State,
  props: {
    shortlistId: string
  },
): ShortlistTrackItem[] =>
  state.shortlists.byId[props.shortlistId]?.items ?? EMPTY_ARRAY
/**
 * Gets sections from a specific shortlist.
 * @param state - The Redux state
 * @param props - Object containing the shortlist ID
 * @param props.shortlistId - The shortlist ID to get sections from
 * @returns Array of shortlist sections
 */
export const getShortlistSections = (
  state: State,
  props: {
    shortlistId: string
  },
): ShortlistSection[] =>
  state.shortlists.byId[props.shortlistId]?.sections ?? EMPTY_ARRAY
/**
 * Gets track objects from shortlist items.
 * @param state - The Redux state
 * @param props - Object containing the shortlist ID
 * @returns Array of track objects from the shortlist
 */
export const getShortlistTracks = createSelector(
  [getTracks, getShortlistItems],
  (tracks, items) => {
    return items.map(item =>
      !item.track.tempId ? tracks[item.track.id] : item.track,
    )
  },
)
/**
 * Factory function that creates a selector to get full shortlist items with track data.
 * @returns A selector function for getting shortlist items with complete track objects
 */
export const makeGetFullShortlistItems = (): ((...args: Array<any>) => any) =>
  createSelector([getTracks, getShortlistItems], (tracks, items) => {
    return items.map(item => ({
      ...item,
      track: !item.track.tempId ? tracks[item.track.id] : item.track,
    }))
  })
/**
 * Factory function that creates a selector to get shortlist tracks.
 * @returns A selector function for getting shortlist tracks
 */
export const makeGetShortlistTracks = (): typeof getShortlistTracks =>
  getShortlistTracks
/**
 * Gets the count of visible shortlists.
 * @param state - The Redux state
 * @returns Number of visible shortlists
 */
export const getVisibleShortlistCount = (state: State): number =>
  state.shortlists.visibleItems.length
/**
 * Checks if any visible shortlists have uploading items.
 * @param state - The Redux state
 * @returns True if any visible shortlist has uploading items
 */
export const getShortlistsUploadStatus = createSelector(
  getShortlistsObject,
  shortlists => {
    const items = Object.keys(shortlists)
      .map(id => shortlists[id])
      .filter((s): s is Shortlist => s != null && !s.hidden)
    return hasUploading(items)
  },
)
/**
 * Gets the IDs of all visible shortlists.
 * @param state - The Redux state
 * @returns Array of visible shortlist IDs
 */
export const getVisibleShortlistIds = (state: State): string[] =>
  state.shortlists.visibleItems
/**
 * Checks if a shortlist has active uploads.
 * @param state - The Redux state
 * @param props - Object containing the shortlist ID
 * @returns True if the shortlist has active uploads
 */
export const shortlistHasActiveUploads = createSelector(
  [getShortlistById],
  shortlist => (shortlist ? hasUploadingInShortlist(shortlist) : false),
)
/**
 * Gets the upload progress for a shortlist.
 * @param state - The Redux state
 * @param props - Object containing the shortlist ID
 * @returns Upload progress percentage, or undefined if no shortlist
 */
export const getShortlistUploadProgress = createSelector(
  [getShortlistById],
  shortlist => {
    if (!shortlist) return undefined
    const totalItems = shortlist.items.length
    const progresses = shortlist.tempItems?.map(item => item.progress) || []
    let progress = 100
    progresses.forEach(p => {
      progress -= (100 - p) / totalItems
    })
    return progress
  },
)
/**
 * Gets all track IDs from all shortlists.
 * @param state - The Redux state
 * @returns Array of all track IDs across shortlists
 */
export const getShortlistTrackIds = createSelector(
  [getShortlistsObject],
  shortlists => {
    const items = Object.keys(shortlists)
      .map(id => shortlists[id])
      .filter((s): s is Shortlist => s != null)
    const result = items.map(s => s.items.map(item => item.track.id)).flat()
    return result
  },
)
/**
 * Gets visible shortlists array.
 * @param state - The Redux state
 * @returns Array of visible shortlist objects
 * @deprecated Avoid using this selector, added for backwards compatibility
 */
export const getShortlists: (state: State) => Shortlist[] = createSelector(
  [getVisibleShortlistIds, getShortlistsObject],
  (ids, dict) =>
    ids.map(id => dict[id]).filter((s): s is Shortlist => s != null),
)
/**
 * Gets shortlists that have no items.
 * @param state - The Redux state
 * @returns Array of empty shortlist objects
 */
export const getEmptyShortlists: (state: State) => Shortlist[] = createSelector(
  [getShortlists],
  (shortlists: Shortlist[]) => {
    return shortlists.filter(shortlist => shortlist?.items?.length === 0)
  },
)

/**
 * Extracts temp item ID from props.
 * @param _ - Unused state parameter
 * @param props - Object containing temp item ID
 * @param props.tempItemId - The temp item ID to extract
 * @returns The temp item ID
 */
const getTempItemId = (
  _: State,
  props: {
    tempItemId?: string
  },
): string | null | undefined => props.tempItemId

/**
 * Gets a temporary item from a shortlist by temp item ID.
 * @param state - The Redux state
 * @param props - Object containing temp item ID
 * @returns The temporary item, or undefined if not found
 */
export const getShortlistTempItem = createSelector(
  [getShortlistById, getTempItemId],
  (shortlist, tempItemId) => {
    return (shortlist?.tempItems ?? []).find(item => item.tempId === tempItemId)
  },
)
/**
 * Gets upload task IDs for a shortlist.
 * @param state - The Redux state
 * @param props - Object containing the shortlist ID
 * @param props.id - The shortlist ID to get upload task IDs for
 * @returns Array of upload task IDs
 */
export const getShortlistUploadTaskIds: (
  arg0: State,
  arg1: {
    id: string | null | undefined
  },
) => string[] = createSelector(
  [getShortlistByIdWithStaleTracks],
  (shortlist: Shortlist | null | undefined) => {
    if (!shortlist) return EMPTY_ARRAY
    return shortlist.items
      .map(item => item.tempId)
      .filter((id): id is string => id != null)
  },
)
/**
 * Gets a temporary item or track for preview by temp item ID.
 * @param state - The Redux state
 * @param props - Object containing temp item ID
 * @param props.tempItemId - The temp item ID to find
 * @returns The track or temp item for preview, or null if not found
 */
export const getShortlistTempItemForPreview: (
  arg0: State,
  arg1: {
    tempItemId?: string
  },
) => (Track | TempItem) | null | undefined = createSelector(
  [getShortlistsObject, getTracks, getTempItemId],
  (shortlistsObject, tracks, tempItemId) => {
    if (!tempItemId) return null
    const shortlists: Shortlist[] = Object.keys(shortlistsObject)
      .map((id: string) => shortlistsObject[id])
      .filter((s): s is Shortlist => s != null)
    const tempItems: TempItem[] = shortlists
      .map((shortlist: Shortlist) => shortlist.tempItems ?? [])
      .flat()
    const tempItem: TempItem | null | undefined = tempItems.find(
      (item: TempItem) => item.tempId === tempItemId,
    )
    const trackItems: Track[] = shortlists
      .map((shortlist: Shortlist) =>
        shortlist.items.map((item: ShortlistTrackItem) => item.track),
      )
      .flat()
    const track: Track | null | undefined = trackItems.find(
      (track: Track) =>
        track.tempId === tempItemId || track.uploadId === tempItemId,
    )
    if (!track) return null
    return track.id ? tracks[track.id] : tempItem
  },
)
/**
 * Checks if track nesting is enabled for a specific shortlist.
 * @param state - The Redux state
 * @param shortlistId - The shortlist ID to check
 * @returns True if nesting is enabled for the shortlist
 */
export const isNestingEnabledInShortlist = (
  state: State,
  shortlistId: string,
): boolean => {
  const nestingEnabled = isFeatureEnabled(state, {
    featureName: TRACK_NESTING,
  })
  const shortlist = getShortlistById(state, {
    id: shortlistId,
  })
  if (!shortlist) return nestingEnabled
  return (
    nestingEnabled &&
    Boolean(shortlist.include_nested_track_versions || shortlist.share_stems)
  )
}
/**
 * Gets default playlist options from user settings.
 * @param state - The Redux state
 * @returns Object containing default playlist options (lyrics, logo, contacts)
 */
export const getDefaultPlaylistOptions = createSelector(
  getUser,
  (user: User) => {
    return {
      // Default values have been discussed in https://disco-team.atlassian.net/browse/DISCO-1639
      show_lyrics:
        user?.settings?.defaultPlaylistOptions?.show_lyrics ??
        user?.business?.private_metadata?.show_lyrics_default,
      show_business_logo:
        user?.settings?.defaultPlaylistOptions?.show_business_logo ?? null,
      show_contacts: user?.settings?.include_custom_message,
    }
  },
)
